import { Component, OnInit } from '@angular/core';
import { BoardPerformanceService } from 'src/app/core/services/board-performance.service';
import { BoardPerformance } from 'src/app/core/models/board-performance.model';
import { LocationPerformance } from 'src/app/core/models/location-performance.model';
import { ThirtyDaySegment } from 'src/app/core/models/thirty-day-segment.model';

@Component({
  selector: 'app-board-stats',
  templateUrl: './boardStats.component.html',
  styleUrls: ['./boardStats.component.scss'],
})
export class BoardStatsComponent implements OnInit {
  // window
  days = 90;
  boardCount: number = 0;
  locationCount: number = 0;


  // loading state
  loading = true;
  loadingBoards = false;
  loadingLocations = false;

  // data
  boardStats: BoardPerformance[] = [];
  originalBoardStats: BoardPerformance[] = [];
  locationStats: LocationPerformance[] = [];
  originalLocationStats: LocationPerformance[] = [];

  // filters
  filtersBoard = {
    assetTag: '',
    serialNumber: '',
    locationName: '',
    city: '',
    state: '',
    salesRepName: '',
    category: ''
  };
  filtersLocation = {
    locationName: '',
    city: '',
    state: '',
    salesRepName: '',
    category: ''
  };

  pageFilter = {
  cashInPerDayMin: null as number | null,
  cashInPerDayMax: null as number | null,
  salesRepName: '',
  state: '',
  locationName: ''
};


  // expand + segment caches
  expandedBoards = new Set<string>();                      // assetTag
  expandedLocations = new Set<string>();                   // locationId
  boardSegs: Record<string, ThirtyDaySegment[]> = {};      // assetTag -> segments
  locationSegs: Record<string, ThirtyDaySegment[]> = {};   // locationId -> segments
  loadingBoardSegs = new Set<string>();
  loadingLocationSegs = new Set<string>();

  // show/hide sections
  showBoardStats = true;
  showLocationStats = true;

  constructor(private perf: BoardPerformanceService) {}

  ngOnInit(): void {
    this.reloadAll();
  }

  // ---- load
  reloadAll(): void {
    this.loading = true;
    this.loadingBoards = true;
    this.loadingLocations = true;

    // clear expansions on reload (fresh segments per window)
    this.expandedBoards.clear();
    this.expandedLocations.clear();
    this.boardSegs = {};
    this.locationSegs = {};
    this.loadingBoardSegs.clear();
    this.loadingLocationSegs.clear();

    this.perf.getBoardPerformance(this.days).subscribe({
      next: (rows) => {
       this.originalBoardStats = rows ?? [];
        this.applyBoardFilters();     
        this.loadingBoards = false;
        this.finishLoadingCheck();
      },
      error: (err) => {
        console.error('Error fetching board performance:', err);
        this.loadingBoards = false;
        this.finishLoadingCheck();
      }
    });

    this.perf.getLocationPerformance(this.days).subscribe({
      next: (rows) => {
        this.originalLocationStats = rows ?? [];
        this.applyLocationFilters();   
        this.loadingLocations = false;
        this.finishLoadingCheck();
      },
      error: (err) => {
        console.error('Error fetching location performance:', err);
        this.loadingLocations = false;
        this.finishLoadingCheck();
      }
    });
  }

  private finishLoadingCheck() {
    if (!this.loadingBoards && !this.loadingLocations) {
      this.loading = false;
    }
  }

  // ---- filters
applyBoardFilters(): void {
  const pf = this.pageFilter;

  const f = {
    assetTag:      this.norm(this.filtersBoard.assetTag),
    serialNumber:  this.norm(this.filtersBoard.serialNumber),
    locationName:  this.norm(this.filtersBoard.locationName),
    city:          this.norm(this.filtersBoard.city),
    state:         this.norm(this.filtersBoard.state),
    salesRepName:  this.norm(this.filtersBoard.salesRepName),
    category:      this.norm(this.filtersBoard.category),
  };

  this.boardStats = this.originalBoardStats.filter(stat => {
    // page-wide text filters
    if (pf.salesRepName && !this.norm(stat.salesRepName).includes(this.norm(pf.salesRepName))) return false;
    if (pf.state       && !this.norm(stat.state).includes(this.norm(pf.state))) return false;
    if (pf.locationName&& !this.norm(stat.locationName).includes(this.norm(pf.locationName))) return false;

    // page-wide numeric range on board Cash In/Day
    if ((pf.cashInPerDayMin != null || pf.cashInPerDayMax != null) &&
        !this.inRange(stat.cashInPerDay, pf.cashInPerDayMin, pf.cashInPerDayMax)) return false;

    // table-specific filters
    return (
      (!f.assetTag     || this.norm(stat.assetTag).includes(f.assetTag)) &&
      (!f.serialNumber || this.norm(stat.serialNumber).includes(f.serialNumber)) &&
      (!f.locationName || this.norm(stat.locationName).includes(f.locationName)) &&
      (!f.city         || this.norm(stat.city).includes(f.city)) &&
      (!f.state        || this.norm(stat.state).includes(f.state)) &&
      (!f.salesRepName || this.norm(stat.salesRepName).includes(f.salesRepName)) &&
      (!f.category     || this.norm(stat.performanceCategory).includes(f.category))
    );
  });
   this.boardCount = this.boardStats.length;
}

applyLocationFilters(): void {
  const pf = this.pageFilter;

  // Which locations have at least one board in Cash In/Day range (respecting page text filters)?
  let allowedLocKeys: Set<string> | null = null;
  if (pf.cashInPerDayMin != null || pf.cashInPerDayMax != null) {
    allowedLocKeys = new Set<string>();
    for (const b of this.originalBoardStats) {
      if (pf.salesRepName && !this.norm(b.salesRepName).includes(this.norm(pf.salesRepName))) continue;
      if (pf.state       && !this.norm(b.state).includes(this.norm(pf.state))) continue;
      if (pf.locationName&& !this.norm(b.locationName).includes(this.norm(pf.locationName))) continue;
      if (this.inRange(b.cashInPerDay, pf.cashInPerDayMin, pf.cashInPerDayMax)) {
        allowedLocKeys.add(this.locKey(b.locationName, b.city, b.state));
      }
    }
  }

  const f = {
    locationName: this.norm(this.filtersLocation.locationName),
    city:         this.norm(this.filtersLocation.city),
    state:        this.norm(this.filtersLocation.state),
    salesRepName: this.norm(this.filtersLocation.salesRepName),
    category:     this.norm(this.filtersLocation.category),
  };

  this.locationStats = this.originalLocationStats.filter(loc => {
    // page-wide text filters
    if (pf.salesRepName && !this.norm(loc.salesRepName).includes(this.norm(pf.salesRepName))) return false;
    if (pf.state       && !this.norm(loc.state).includes(this.norm(pf.state))) return false;
    if (pf.locationName&& !this.norm(loc.locationName).includes(this.norm(pf.locationName))) return false;

    // if a range is set, keep only locations that have at least one board in range
    if (allowedLocKeys && !allowedLocKeys.has(this.locKey(loc.locationName, loc.city, loc.state))) return false;

    // table-specific filters
    return (
      (!f.locationName || this.norm(loc.locationName).includes(f.locationName)) &&
      (!f.city         || this.norm(loc.city).includes(f.city)) &&
      (!f.state        || this.norm(loc.state).includes(f.state)) &&
      (!f.salesRepName || this.norm(loc.salesRepName).includes(f.salesRepName)) &&
      (!f.category     || this.norm(loc.performanceCategory).includes(f.category))
    );
  });
  this.locationCount = this.locationStats.length;

}


  // ---- exports
  downloadBoardExcel(): void {
    this.perf.exportToExcelBoard(this.days).subscribe(blob => {
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `board-performance_${this.days}d.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    });
  }

  downloadLocationExcel(): void {
    this.perf.exportToExcelLocation(this.days).subscribe(blob => {
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `location-performance_${this.days}d.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    });
  }

  // ---- expand boards
  toggleBoard(assetTag: string) {
    if (this.expandedBoards.has(assetTag)) {
      this.expandedBoards.delete(assetTag);
      return;
    }
    this.expandedBoards.add(assetTag);
    if (!this.boardSegs[assetTag] && !this.loadingBoardSegs.has(assetTag)) {
      this.loadingBoardSegs.add(assetTag);
      this.perf.getBoardSegments30(assetTag, this.days).subscribe({
        next: segs => {
          this.boardSegs[assetTag] = segs ?? [];
          this.loadingBoardSegs.delete(assetTag);
        },
        error: err => {
          console.error(`segments for board ${assetTag} failed:`, err);
          this.boardSegs[assetTag] = [];
          this.loadingBoardSegs.delete(assetTag);
        }
      });
    }
  }

  // ---- expand locations
  toggleLocation(locationId: string) {
    if (this.expandedLocations.has(locationId)) {
      this.expandedLocations.delete(locationId);
      return;
    }
    this.expandedLocations.add(locationId);
    if (!this.locationSegs[locationId] && !this.loadingLocationSegs.has(locationId)) {
      this.loadingLocationSegs.add(locationId);
      this.perf.getLocationSegments30(locationId, this.days).subscribe({
        next: segs => {
          this.locationSegs[locationId] = segs ?? [];
          this.loadingLocationSegs.delete(locationId);
        },
        error: err => {
          console.error(`segments for location ${locationId} failed:`, err);
          this.locationSegs[locationId] = [];
          this.loadingLocationSegs.delete(locationId);
        }
      });
    }
  }

  // ---- totals (segments)
// add/replace your current sumSegments with this
sumSegments(segs: any[]) {
  const t = { spanDays: 0, cashIn: 0, cashOut: 0, net: 0 };
  for (const s of segs || []) {
    t.spanDays += Number(s.spanDays || 0);
    t.cashIn   += Number(s.cashIn   || 0);
    t.cashOut  += Number(s.cashOut  || 0);
    t.net      += Number(s.net      || 0);
  }
  return t;
}

// Put this in your component class
private norm(s: any): string {
  return (s ?? '')
    .toString()
    .normalize('NFKD')              // split accents (é -> e + ́)
    .toLowerCase()                  // case-insensitive
    .replace(/\p{Diacritic}/gu, '') // drop accents
    .replace(/\s+/g, ' ')           // collapse spaces
    .trim();                        // trim ends
}

applyAllFilters(): void {
  this.applyBoardFilters();
  this.applyLocationFilters();
}


private inRange(val: number | null | undefined, min: number | null, max: number | null) {
  if (val == null) return false;
  if (min != null && val < min) return false;
  if (max != null && val > max) return false;
  return true;
}

private locKey(name?: string, city?: string, state?: string) {
  // normalize to make matching forgiving
  const n = (s: any) => this.norm(s);
  return `${n(name)}|${n(city)}|${n(state)}`;
}

// --- tiny CSV helpers ---
private csvEscape(v: any): string {
  if (v === null || v === undefined) return '';
  const s = String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

private downloadCsv(filename: string, rows: any[], cols: { header: string; key: string; map?: (row:any)=>any }[]) {
  const header = cols.map(c => this.csvEscape(c.header)).join(',');
  const body = rows.map(r =>
    cols.map(c => {
      const val = c.map ? c.map(r) : r[c.key];
      return this.csvEscape(val);
    }).join(',')
  ).join('\n');

  const blob = new Blob([header + '\n' + body], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

// --- export the CURRENTLY FILTERED board table ---
exportFilteredBoardsCsv(): void {
  const cols = [
    { header: 'Category',        key: 'performanceCategory' },
    { header: 'Asset Tag',       key: 'assetTag' },
    { header: 'Location',        key: 'locationName' },
    { header: 'City',            key: 'city' },
    { header: 'State',           key: 'state' },
    { header: 'Sales Rep',       key: 'salesRepName' },
    { header: 'Total Cash In',   key: 'totalCashIn' },
    { header: 'Total Cash Out',  key: 'totalCashOut' },
    { header: 'Total Net',       key: 'totalNet' },
    { header: 'Days Active',     key: 'daysActive' },
    { header: '$/Day',           key: 'cashInPerDay',   map: r => (r.cashInPerDay ?? 0).toFixed(2) },
    { header: 'Compared $/Day',  key: 'comparisonAverage', map: r => (r.comparisonAverage ?? 0).toFixed(2) },
    { header: 'Net/Day',         key: 'netPerDay',      map: r => (r.netPerDay ?? 0).toFixed(2) },
  ];
  this.downloadCsv(`board-performance_filtered_${this.days}d.csv`, this.boardStats, cols);
}

// --- export the CURRENTLY FILTERED location table ---
exportFilteredLocationsCsv(): void {
  const cols = [
    { header: 'Category',        key: 'performanceCategory' },
    { header: 'Location',        key: 'locationName' },
    { header: 'City',            key: 'city' },
    { header: 'State',           key: 'state' },
    { header: 'Sales Rep',       key: 'salesRepName' },
    { header: 'Total Cash In',   key: 'totalCashIn' },
    { header: 'Total Cash Out',  key: 'totalCashOut' },
    { header: 'Total Net',       key: 'totalNet' },
    { header: 'Days Active',     key: 'daysActive' },
    { header: 'Board Count',     key: 'boardCount' },
    { header: 'Cash In/Day',     key: 'cashInPerDay',   map: r => (r.cashInPerDay ?? 0).toFixed(2) },
    { header: 'Net/Day',         key: 'netPerDay',      map: r => (r.netPerDay ?? 0).toFixed(2) },
  ];
  this.downloadCsv(`location-performance_filtered_${this.days}d.csv`, this.locationStats, cols);
}





  // trackBy
  trackByAssetTag = (_: number, row: BoardPerformance) => row.assetTag;
  trackByLocationId = (_: number, row: LocationPerformance) => (row as any).locationId || `${row.locationName}|${row.city}|${row.state}`;
}
